import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown, Plus } from "lucide-react";
import { NAV, SERVICE_GROUPS, INDUSTRIES } from "../../data/site";

const ease = [0.16, 1, 0.3, 1];
const DROPDOWNS = { Services: "/services", Industries: "/industries" };

function Panel({ children, wide }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.28, ease }}
      className={`absolute left-0 top-full pt-4 ${wide ? "w-[720px]" : "w-[300px]"}`}
    >
      <div className="border border-border bg-white p-6 shadow-2xl">{children}</div>
    </motion.div>
  );
}

function DesktopDropdown({ label, to, open, setOpen }) {
  const isOpen = open === label;
  const timer = useRef(null);
  const enter = () => { clearTimeout(timer.current); setOpen(label); };
  const leave = () => { timer.current = setTimeout(() => setOpen((o) => (o === label ? null : o)), 120); };

  return (
    <div
      className="relative"
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocus={enter}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen((o) => (o === label ? null : o)); }}
    >
      <Link
        to={to}
        data-testid={`nav-${label.toLowerCase()}`}
        className={`inline-flex items-center gap-1 font-mono text-xs uppercase tracking-[0.18em] transition-colors ${isOpen ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
        aria-expanded={isOpen}
      >
        {label}
        <ChevronDown size={13} className={`transition-transform duration-300 ${isOpen ? "rotate-180 text-brand" : ""}`} />
      </Link>

      <AnimatePresence>
        {isOpen && label === "Services" && (
          <Panel wide>
            <div className="grid grid-cols-3 gap-6">
              {SERVICE_GROUPS.map((g) => (
                <div key={g.label}>
                  <div className="mb-3 border-b border-border pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brand">{g.label}</div>
                  <ul className="space-y-1">
                    {g.items.map((s) => (
                      <li key={s.slug}>
                        <Link
                          to={`/services#${s.slug}`}
                          data-testid={`nav-service-${s.slug}`}
                          className="group flex items-start gap-2 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                        >
                          <span className="font-mono text-[10px] text-brand/70 pt-0.5">{s.id}</span>
                          <span className="leading-snug">{s.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">12 workforce services</span>
              <Link to="/services" className="group inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] text-foreground hover:text-brand">
                View all <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Panel>
        )}
        {isOpen && label === "Industries" && (
          <Panel>
            <ul className="space-y-1">
              {INDUSTRIES.map((ind) => (
                <li key={ind.slug}>
                  <Link
                    to={`/industries#${ind.slug}`}
                    data-testid={`nav-industry-${ind.slug}`}
                    className="group flex items-center justify-between py-2 transition-colors"
                  >
                    <div>
                      <div className="text-sm text-muted-foreground transition-colors group-hover:text-foreground">{ind.name}</div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground/60">{ind.tag}</div>
                    </div>
                    <ArrowUpRight size={14} className="text-muted-foreground opacity-0 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100 group-hover:text-brand" />
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileGroup({ label, items, renderItem, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/60">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`mobile-acc-${testid}`}
        className="flex w-full items-center justify-between py-4 font-display text-2xl tracking-tight"
      >
        {label}
        <Plus size={20} className={`transition-transform duration-300 ${open ? "rotate-45 text-brand" : "text-muted-foreground"}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease }} className="overflow-hidden">
            <div className="pb-4">{items.map(renderItem)}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMenu(null); }, [loc.pathname, loc.hash]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setMenu(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${scrolled || menu ? "bg-white/85 backdrop-blur-xl border-b border-border shadow-sm" : "bg-transparent"}`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/" data-testid="logo-link" className="group flex items-center gap-2.5">
          <img src="/vbg-bridge-mark-t.png" alt="VectorBridge Group" draggable={false} className="pointer-events-none h-10 w-auto select-none transition-transform duration-500 group-hover:scale-105" />
          <span className="flex flex-col leading-none">
            <span className="font-display text-lg font-bold uppercase tracking-tight text-navy">VectorBridge</span>
            <span className="-mt-0.5 font-mono text-[9px] uppercase tracking-[0.3em] text-muted-foreground">Group, LLC</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:ml-24 lg:flex xl:ml-36">
          {NAV.map((n) =>
            DROPDOWNS[n.label] ? (
              <DesktopDropdown key={n.to} label={n.label} to={n.to} open={menu} setOpen={setMenu} />
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `link-underline font-mono text-xs uppercase tracking-[0.18em] transition-colors ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <Link to="/candidate-portal" data-testid="nav-candidate-portal" className="border border-border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:border-foreground hover:text-foreground">Candidate</Link>
          <Link to="/client-portal" data-testid="nav-client-portal" className="group inline-flex items-center gap-1 bg-brand px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy">
            Client Portal <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className="grid h-10 w-10 place-items-center border border-border lg:hidden" aria-label="Menu">
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease }} className="max-h-[80vh] overflow-y-auto border-t border-border bg-white backdrop-blur-xl lg:hidden">
            <div className="flex flex-col px-5 py-3">
              <Link to="/about" data-testid="mobile-nav-about" className="border-b border-border/60 py-4 font-display text-2xl tracking-tight">About</Link>
              <Link to="/government" data-testid="mobile-nav-government" className="border-b border-border/60 py-4 font-display text-2xl tracking-tight">Government</Link>

              <MobileGroup
                label="Services" testid="services"
                items={SERVICE_GROUPS.flatMap((g) => g.items)}
                renderItem={(s) => (
                  <Link key={s.slug} to={`/services#${s.slug}`} data-testid={`mobile-service-${s.slug}`} className="flex items-center gap-3 py-2 text-sm text-muted-foreground">
                    <span className="font-mono text-[10px] text-brand">{s.id}</span> {s.title}
                  </Link>
                )}
              />
              <MobileGroup
                label="Industries" testid="industries"
                items={INDUSTRIES}
                renderItem={(ind) => (
                  <Link key={ind.slug} to={`/industries#${ind.slug}`} data-testid={`mobile-industry-${ind.slug}`} className="block py-2 text-sm text-muted-foreground">{ind.name}</Link>
                )}
              />

              {[{ label: "Careers", to: "/careers" }, { label: "Contact", to: "/contact" }, { label: "Client Portal", to: "/client-portal" }, { label: "Candidate Portal", to: "/candidate-portal" }].map((n) => (
                <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className="border-b border-border/60 py-4 font-display text-2xl tracking-tight">{n.label}</Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
